// ShortcutCheatsheet — modal s prehladom vsetkych aktivnych klavesovych skratiek
// Ustava sek. 54.8: stlacenie '?' kdekoľvek otvori cheatsheet
// Auto-generated zo registracnej tabulky (single source of truth)
// Hladanie filtruje, button "Vytlacit" pripravi A4 (sek. 26)

import React, { useState, useEffect, useMemo } from 'react'
import { useShortcutsRegistry, formatKeys, useShortcuts } from '../hooks/useShortcuts.js'
import { COLORS, RADIUS, TYPO, SPACE, SHADOW, Z_INDEX, hladaj } from '../theme.js'
import { Btn, BtnGhost } from './Btn.jsx'

// Globalne riadenie cheatsheet (otvoreny / zatvoreny)
let _setOpen = null
export function openCheatsheet() { if (_setOpen) _setOpen(true) }
export function closeCheatsheet() { if (_setOpen) _setOpen(false) }

export function ShortcutCheatsheet() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const registry = useShortcutsRegistry()

  // Expose setOpen pre globalne ovladanie
  useEffect(() => { _setOpen = setOpen; return () => { _setOpen = null } }, [])

  // Registracia '?' a 'Esc' (Esc zatvara modal, '?' otvara)
  // Tieto su zaregistrovane samostatne aby boli vzdy dostupne.
  useShortcuts(useMemo(() => ([
    { keys: '?',           label: 'Klávesové skratky',        scope: 'global', handler: () => setOpen(o => !o), preventDefault: false },
    { keys: 'shift+slash', label: 'Klávesové skratky (US)',   scope: 'global', handler: () => setOpen(o => !o), preventDefault: false }
  ]), []))

  // Esc handler
  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false) } }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  if (!open) return null

  // Filter + grupovanie podla scope
  const filtered = registry.filter(s => {
    if (!query) return true
    const q = query.toLowerCase()
    return hladaj((s.label || ''), q) ||
           hladaj((s.keys || ''), q) ||
           hladaj((s.scope || ''), q)
  })

  const grouped = {}
  filtered.forEach(s => {
    const scope = s.scope || 'global'
    if (!grouped[scope]) grouped[scope] = []
    grouped[scope].push(s)
  })

  const scopeOrder = ['global', ...Object.keys(grouped).filter(k => k !== 'global').sort()]

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cheatsheet-title"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: SPACE.xl,
        paddingTop: '5vh',
        zIndex: Z_INDEX.cheatsheet
      }}
    >
      <div style={{
        background: COLORS.bg,
        borderRadius: RADIUS.lg,
        boxShadow: SHADOW.modal,
        width: 'min(720px, 95vw)',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: `${SPACE.lg}px ${SPACE.xl}px`,
          borderBottom: `1px solid ${COLORS.line}`,
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.md
        }}>
          <h2 id="cheatsheet-title" style={{
            margin: 0,
            fontSize: TYPO.sizes.lg,
            fontWeight: TYPO.weights.bold,
            color: COLORS.text
          }}>Klávesové skratky</h2>
          <span style={{ flex: 1 }} />
          <BtnGhost size="sm" onClick={() => window.print()}>Vytlačiť</BtnGhost>
          <button
            onClick={() => setOpen(false)}
            aria-label="Zavriet"
            style={{
              background: 'transparent', border: 'none',
              fontSize: 24, lineHeight: 1, color: COLORS.muted,
              cursor: 'pointer', padding: 0, width: 30, height: 30
            }}
          >×</button>
        </div>

        <div style={{ padding: `${SPACE.md}px ${SPACE.xl}px`, borderBottom: `1px solid ${COLORS.lineSoft}` }}>
          <input
            autoFocus
            type="text"
            placeholder="Hľadať skratku alebo akciu…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: TYPO.sizes.base,
              fontFamily: 'inherit',
              border: `1px solid ${COLORS.line}`,
              borderRadius: RADIUS.md,
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: SPACE.xl }}>
          {scopeOrder.length === 0 || filtered.length === 0 ? (
            <p style={{ color: COLORS.muted, textAlign: 'center', padding: SPACE.xl }}>
              Žiadne skratky {query && `pre „${query}"`}.
            </p>
          ) : (
            scopeOrder.map(scope => grouped[scope] && (
              <div key={scope} style={{ marginBottom: SPACE.xl }}>
                <h3 style={{
                  margin: `0 0 ${SPACE.sm}px`,
                  fontSize: TYPO.sizes.sm,
                  fontWeight: TYPO.weights.semibold,
                  color: COLORS.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em'
                }}>{scope === 'global' ? 'Globálne' : scope}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {grouped[scope].map(s => (
                      <tr key={s.id} style={{ borderTop: `1px solid ${COLORS.lineSoft}` }}>
                        <td style={{ padding: '6px 0', width: 180 }}>
                          <kbd style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            background: COLORS.bgSoft,
                            border: `1px solid ${COLORS.line}`,
                            borderRadius: RADIUS.sm,
                            fontFamily: TYPO.mono,
                            fontSize: TYPO.sizes.sm,
                            color: COLORS.text,
                            fontWeight: TYPO.weights.semibold
                          }}>{formatKeys(s.keys)}</kbd>
                        </td>
                        <td style={{ padding: '6px 0', color: COLORS.text }}>
                          {s.label}
                          {s.description && <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm, marginLeft: SPACE.sm }}>{s.description}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>

        <div style={{
          padding: `${SPACE.sm}px ${SPACE.xl}px`,
          borderTop: `1px solid ${COLORS.line}`,
          background: COLORS.bgSoft,
          fontSize: TYPO.sizes.xs,
          color: COLORS.muted
        }}>
          Stlač <kbd style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, padding: '0 4px', borderRadius: 3 }}>?</kbd> kedykoľvek pre tento prehľad · ústava sek. 54.8
        </div>
      </div>
    </div>
  )
}

export default ShortcutCheatsheet
